import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  LoaderCircle,
  Minus,
  PanelRightClose,
  Plus,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
} from "react";
import { useApi } from "../App.jsx";

function clampPage(value, total) {
  return Math.max(1, Math.min(Number(value) || 1, total || 1));
}

export function LibraryPdfPreview({ document, onClose, onPageChange }) {
  const api = useApi();
  const notebookId = document?.notebookId || null;
  const sourceId = document?.sourceId || null;
  const requestedPage = document?.page || 1;
  const bodyRef = useRef(null);
  const canvasRef = useRef(null);
  const observedWidthRef = useRef(0);
  const resizeFrameRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [renderVersion, setRenderVersion] = useState(0);
  const [state, setState] = useState({
    loading: false,
    rendering: false,
    error: "",
  });

  useEffect(() => {
    if (!document) {
      setPdf(null);
      setState({ loading: false, rendering: false, error: "" });
      return undefined;
    }

    let active = true;
    let loadedPdf;
    setPdf(null);
    setPage(requestedPage);
    setZoom(1);
    setState({ loading: true, rendering: false, error: "" });

    Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
      api.getNotebookSourceBlob(notebookId, sourceId),
    ])
      .then(async ([
        { getDocument, GlobalWorkerOptions },
        workerModule,
        file,
      ]) => {
        GlobalWorkerOptions.workerSrc = `${workerModule.default}?module=1`;
        loadedPdf = await getDocument({
          data: new Uint8Array(await file.arrayBuffer()),
          isEvalSupported: false,
        }).promise;
        if (!active) {
          await loadedPdf.destroy();
          return;
        }
        setPage(clampPage(requestedPage, loadedPdf.numPages));
        setPdf(loadedPdf);
        setState({ loading: false, rendering: true, error: "" });
      })
      .catch((error) => {
        if (active) {
          setState({
            loading: false,
            rendering: false,
            error: error.message || "No pudimos abrir el PDF.",
          });
        }
      });

    return () => {
      active = false;
      if (loadedPdf) void loadedPdf.destroy();
    };
  }, [api, notebookId, sourceId]);

  useEffect(() => {
    if (pdf) setPage(clampPage(requestedPage, pdf.numPages));
  }, [pdf, requestedPage]);

  useEffect(() => {
    if (!document) {
      onPageChange?.(null);
      return;
    }
    onPageChange?.({
      sourceId,
      pageLabel: String(page),
      displayedPage: page,
      viewerPage: page,
      viewerPageIndex: page - 1,
      physicalPage: page,
      totalPages: pdf?.numPages || null,
    });
  }, [document, onPageChange, page, pdf?.numPages, sourceId]);

  useEffect(() => {
    const element = bodyRef.current;
    if (!element || !window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const borderBox = Array.isArray(entry.borderBoxSize)
        ? entry.borderBoxSize[0]
        : entry.borderBoxSize;
      const width = Math.round(
        borderBox?.inlineSize || entry.target.getBoundingClientRect().width,
      );
      if (Math.abs(width - observedWidthRef.current) < 2) return;
      observedWidthRef.current = width;
      if (resizeFrameRef.current) {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        setRenderVersion((current) => current + 1);
      });
    });
    observer.observe(element, { box: "border-box" });
    return () => {
      observer.disconnect();
      if (resizeFrameRef.current) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [notebookId, sourceId]);

  useEffect(() => {
    if (!pdf || !canvasRef.current || !bodyRef.current) return undefined;
    let active = true;
    let renderTask;
    setState((current) => ({ ...current, rendering: true, error: "" }));

    pdf.getPage(page)
      .then((pdfPage) => {
        if (!active) return;
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const availableWidth = Math.max(bodyRef.current.clientWidth - 28, 240);
        const fitScale = availableWidth / baseViewport.width;
        const viewport = pdfPage.getViewport({ scale: fitScale * zoom });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        const pixelRatio = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        renderTask = pdfPage.render({
          canvasContext: context,
          viewport,
          transform: pixelRatio === 1
            ? null
            : [pixelRatio, 0, 0, pixelRatio, 0, 0],
        });
        return renderTask.promise;
      })
      .then(() => {
        if (active) {
          setState((current) => ({ ...current, rendering: false }));
        }
      })
      .catch((error) => {
        if (!active || error?.name === "RenderingCancelledException") return;
        setState((current) => ({
          ...current,
          rendering: false,
          error: error.message || "No pudimos mostrar esta página.",
        }));
      });

    return () => {
      active = false;
      renderTask?.cancel();
    };
  }, [page, pdf, renderVersion, zoom]);

  if (!document) {
    return (
      <aside className="library-ai-preview is-empty">
        <div className="library-ai-preview-empty">
          <span><FileText size={24} /></span>
          <strong>Vista del documento</strong>
          <p>Seleccioná un PDF o una cita de Justiniano para verlo acá.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="library-ai-preview">
      <header>
        <div>
          <span className="eyebrow">
            PDF{document.page ? ` · PÁGINA ${document.page}` : ""}
          </span>
          <strong title={document.fileName}>{document.fileName}</strong>
        </div>
        <div>
          <button
            className="icon-button"
            type="button"
            title="Descargar PDF"
            onClick={() => api.downloadNotebookSource(
              document.notebookId,
              document.sourceId,
            )}
          >
            <Download size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            title="Abrir en otra pestaña"
            onClick={() => api.openNotebookSource(
              document.notebookId,
              document.sourceId,
              page,
            )}
          >
            <ExternalLink size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            title="Cerrar visor"
            onClick={onClose}
          >
            <PanelRightClose size={18} />
          </button>
        </div>
      </header>

      <div className="library-ai-pdf-toolbar">
        <button
          type="button"
          title="Página anterior"
          disabled={!pdf || page <= 1}
          onClick={() => setPage((current) => current - 1)}
        >
          <ChevronLeft size={16} />
        </button>
        <span>
          <strong>{page}</strong>
          <i>/</i>
          {pdf?.numPages || "—"}
        </span>
        <button
          type="button"
          title="Página siguiente"
          disabled={!pdf || page >= pdf.numPages}
          onClick={() => setPage((current) => current + 1)}
        >
          <ChevronRight size={16} />
        </button>
        <em />
        <button
          type="button"
          title="Alejar"
          disabled={!pdf || zoom <= 0.6}
          onClick={() => setZoom((current) =>
            Math.max(0.6, Number((current - 0.2).toFixed(1))))}
        >
          <Minus size={15} />
        </button>
        <span>{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          title="Acercar"
          disabled={!pdf || zoom >= 2}
          onClick={() => setZoom((current) =>
            Math.min(2, Number((current + 0.2).toFixed(1))))}
        >
          <Plus size={15} />
        </button>
      </div>

      <div className="library-ai-preview-body" ref={bodyRef}>
        {(state.loading || state.rendering) && !state.error && (
          <div className="library-ai-preview-loading">
            <LoaderCircle className="is-spinning" size={19} />
            {state.loading ? "Preparando PDF…" : "Mostrando página…"}
          </div>
        )}
        {state.error && (
          <div className="library-ai-preview-status is-error">
            <FileText size={21} />
            <span>{state.error}</span>
            <button
              className="secondary-button"
              type="button"
              onClick={() => api.openNotebookSource(
                document.notebookId,
                document.sourceId,
                page,
              )}
            >
              Abrir en otra pestaña
            </button>
          </div>
        )}
        <canvas
          ref={canvasRef}
          hidden={state.loading || Boolean(state.error)}
        />
      </div>
    </aside>
  );
}
